import { useInterval } from '@solinkcorp/solink-components';
import { useMemo, useRef } from 'react';
import { Listener } from 'src/core/utils/Listener';
import { webRTCSupportedBase } from 'src/core/video/supportedPlayers';
import { type SelectedStream } from 'src/core/video/types';
import { getCredentials, type VideoPlayerType } from 'src/core/video/VideoUtil';
import { getIceServers, getWhepURL } from '../rtspSimpleServerPlayer/WebRTCPlayer';
import { closeWhepSession, WhepSignalling } from '../rtspSimpleServerPlayer/WhepSignalling';
import { type CameraPlayerProps } from '../types';

// 60 minutes
const MAX_INTERVAL = 60 * 60 * 1000;
const INITIAL_INTERVAL = 5 * 60 * 1000;

/**
 * Periodically attempt to establish a WebRTC connection for a camera
 * that is not currently using WebRTC, even though the location supports it. If the
 * connection succeeds, it clears any fallback player type. Interval is exponential backed-off
 * with a maximum of 60 minutes.
 * */
function useRecoverWebRTC(
  { user, playingCamera, locationIsLocal, playbackController, isMuted }: CameraPlayerProps,
  selectedStream: SelectedStream | undefined,
  isPlayingLive: boolean,
  currentPlayerType: VideoPlayerType,
): void {
  const intervalRef = useRef<number>(INITIAL_INTERVAL);
  const playerLog = playbackController.getPlayerLog(playingCamera.playerId);

  const { authQueryParams } = useMemo(() => {
    const credentials = getCredentials(user, {
      type: 'local', // Always choose local, because we can't play WebRTC from the cloud.
      location: playingCamera.location,
    });
    return credentials;
  }, [user, playingCamera.location]);

  const shouldSkipCheck = useMemo(() => {
    return (
      !selectedStream ||
      !isPlayingLive ||
      !user ||
      !isMuted ||
      // WebRTC is already being used
      (webRTCSupportedBase(user, playingCamera.device, playingCamera.camera) &&
        currentPlayerType === 'WebRTC')
    );
  }, [selectedStream, isPlayingLive, user, isMuted, currentPlayerType, playingCamera]);

  useInterval(
    async () => {
      if (!selectedStream || !user) {
        return;
      }

      const whepURL = playingCamera.camera.liveUrl?.includes('/whep')
        ? playingCamera.camera.liveUrl
        : getWhepURL(playingCamera, locationIsLocal, selectedStream.stream.id, authQueryParams);

      if (!whepURL) {
        return;
      }

      playerLog.info(`[useRecoverWebRTC] Checking WebRTC Connection`);

      const listener = new Listener();
      const signalling = new WhepSignalling(whepURL);
      const iceServers = getIceServers(playingCamera.camera?.turnUrl, user);
      const pc = new RTCPeerConnection({ iceServers });

      // create data channel to trigger negotiation needed event
      pc.createDataChannel('testDataChannel');
      playerLog.log(`[useRecoverWebRTC] Creating data channel to test connection`);

      listener.listenTo(pc, 'negotiationneeded', () => {
        playerLog.warn(`[useRecoverWebRTC] Negotiation needed`);

        Promise.resolve()
          .then(async () => {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            const { remoteDescription } = await signalling.sendOffer(offer);
            await pc.setRemoteDescription(remoteDescription);

            playerLog.warn(`[useRecoverWebRTC] WebRTC Connection successful`);

            // clear fallback player type if WebRTC connection is successful
            playbackController.setPlayerTypeFallback({
              playerId: playingCamera.playerId,
              playerType: undefined,
            });
          })
          .catch((error) => {
            // exponential backoff for retrying WebRTC connection
            intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
            playerLog.error(`[useRecoverWebRTC] WebRTC Connection failed: ${error}`);
          })
          .finally(() => {
            playerLog.info(`[useRecoverWebRTC] Closing peer-connection`);
            listener.stopListening();
            void closeWhepSession(signalling, pc);
          });
      });
    },
    shouldSkipCheck ? undefined : intervalRef.current,
    [isPlayingLive, user, playingCamera, playerLog, selectedStream, playbackController],
  );
}

export default useRecoverWebRTC;
